import { FC } from 'react';
import { useHistory, useLocation } from 'react-router';

import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import Loader from 'components/Loader';
import Stepper from 'components/Stepper';
import { StepOptions, StepProps } from 'components/Stepper/types';
import {
  orderNewVirtual,
  orderNewVirtualTemporary,
} from 'features/PaymentCards/routes';

import ErrorView from '../components/ErrorView';
import { VirtualCardOrderFlowContextProvider } from './hooks/useVirtualCardOrderFlowContext';
import { useVirtualCardOrderFlowQuery } from './hooks/useVirtualCardOrderFlowQuery';
import VirtualCardOrderChooseDeactivationDateEdit from './steps/VirtualCardOrderChooseDeactivationDateEdit';
import VirtualCardOrderRecap from './steps/VirtualCardOrderRecap';
import { mapQueryResultToFlowConfig } from './utils';

type Props = {
  path: string;
};

const VirtualCardOrder: FC<Props> = ({ path }) => {
  let steps: StepOptions[];

  switch (path) {
    case orderNewVirtual:
      steps = [
        {
          key: 'order-recap',
          render: (props) => <VirtualCardOrderRecap {...props} />,
        },
      ];
      break;
    case orderNewVirtualTemporary:
      steps = [
        {
          key: 'choose-deactivation-date-edit',
          render: (props: StepProps) => (
            <VirtualCardOrderChooseDeactivationDateEdit {...props} />
          ),
        },
        {
          key: 'order-recap',
          render: (props: StepProps) => <VirtualCardOrderRecap {...props} />,
        },
      ];
      break;
    default:
      throw new Error('The pathname is not correct');
  }

  return <Stepper steps={steps} />;
};

const Container: FC = () => {
  const { goBack } = useHistory();
  const location = useLocation();

  const { data, error, loading } = useVirtualCardOrderFlowQuery();
  const { plan } = useCurrentPricingPlan();

  if (loading) {
    return <Loader />;
  }

  if (error || !data || !plan) {
    return <ErrorView onGoBackPressed={goBack} />;
  }

  const flowConfig = mapQueryResultToFlowConfig(data, plan);

  return (
    <VirtualCardOrderFlowContextProvider value={flowConfig}>
      <VirtualCardOrder path={location.pathname} />
    </VirtualCardOrderFlowContextProvider>
  );
};

export default Container;
